import { useState } from "react";
import {
  ClipboardList,
  Users,
  Calendar,
  Hash,
  Package,
  Pencil,
  User,
  Ban,
  Trash,
} from "lucide-react";
import "./index.css";
import { ErrorText } from "../../../StyledCss/styledComponents";
import { assignUserToProject,deleteUserAssignedToProject,updateEmployeeProjectStatus,updateUserAssignedToProject,getProjectDetails } from "../../../api/services/hroneApi";
import PopupModal from "../../PopupModal";
import { PrimaryButton } from "../../../StyledCss/styledbtn";
import { SecondaryButton } from "../../../StyledCss/styledbtn";
import AddProjectAssignmentForm from "../../AddUserForm/addUserForm";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { convertKeysToCamelCase } from "../../../util/convertCamelCase";
import { convertKeysToSnakeCase } from "../../../util/convertToSnakeCase";
import { usePermissions } from "../../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../../util/featureCodes";
export const STATUS_BADGE_CONFIG = {
  PENDING: {
    label: "Pending",
    className: "bg-warning text-dark",
  },
  ACTIVE: {
    label: "Active",
    className: "bg-success",
  },
  OFFBOARDED: {
    label: "Offboarded",
    className: "bg-danger",
  },
  TERMINATED: {
    label: "Terminated",
    className: "bg-danger",
  },
  DEFAULT: {
    label: "Unknown",
    className: "bg-info",
  },
};
export const EmployeeTable = ({
  employees,
  projectId,
  status,
  startDate,
  onDataRefresh
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = usePermissions();
  const canAssignEmployee = hasPermission(FEATURE_CODES.PROJECT_EMPLOYEE_ASSIGN);
  const canEditEmployee = hasPermission(FEATURE_CODES.PROJECT_EMPLOYEE_EDIT);
  const canReleaseEmployee = hasPermission(FEATURE_CODES.PROJECT_EMPLOYEE_RELEASE);
  const canRemoveEmployee = hasPermission(FEATURE_CODES.PROJECT_EMPLOYEE_REMOVE);
  const canViewAllWorklogs = hasPermission(FEATURE_CODES.WORKLOG_VIEW_ALL);
  const [isUserPopupOpen, setIsUserPopupOpen] = useState(false);
  const [isOffboardModalOpen, setIsOffboardModalOpen] = useState(false);
  const [userToOffboard, setUserToOffboard] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteWorklogCount, setDeleteWorklogCount] = useState(null);
  const [forceDelete, setForceDelete] = useState(false);
  const [isCheckingDelete, setIsCheckingDelete] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editEmployeeData, setEditEmployeeData] = useState(null);
  const [offboardingDate, setOffboardingDate] = useState("");
  const [loadingStates, setLoadingStates] = useState({
    assignEmployee: false,
    editEmployee: false,
    deleteEmployee: false,
    updateEmployeeStatus: false
  });
    const [errorMessages, setErrorMessages] = useState({
    addEmployee: "",
    updateEmployee: "", 
    updateEmployeeStatus: ""

  });

  const setLoading = (operation, isLoading) => {
    setLoadingStates(prev => ({ ...prev, [operation]: isLoading }));
  };

  const refreshData = async () => {
    try {
      const response = await getProjectDetails(projectId);
      if (response.ok) {
        const data = await response.json();
        onDataRefresh(convertKeysToCamelCase(data));
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const handleUserCancel = () => {
    setIsUserPopupOpen(false);
    setIsEditMode(false);
    setEditEmployeeData(null);
    setErrorMessages({ addEmployee: "", updateEmployee: "" });
  };

  const cancelOffboard = () => {
    setUserToOffboard(null);
    setIsOffboardModalOpen(false);
    setOffboardingDate("");
    setErrorMessages({ updateEmployeeStatus: ""});
  };

  const handleDateSubmit = async () => {
    if (userToOffboard && offboardingDate) {

      const payload = {
        project_id: projectId,
        released_at: offboardingDate,
        status: "OFFBOARDED",
        id:userToOffboard?.id
      };

      try {
        setLoading("updateEmployeeStatus", true);
        const response = await updateEmployeeProjectStatus(payload);
        if (response.ok) {
          toast.success("Employee offboarded successfully");
          await refreshData();
          setIsOffboardModalOpen(false);
          setUserToOffboard(null);
          setOffboardingDate("");
        } else {
          const errorData = await response.json();
          setErrorMessages(prev => ({...prev, updateEmployeeStatus: errorData.detail}));
          console.error("API Error:", errorData);
        }
      } catch (error) {
        toast.error("Failed to offboard employee");
      } finally {
        setLoading("updateEmployeeStatus", false);
      }
    }
  };

  const handleUserSubmit = async (userData) => {
    const payload = {
      projectId: projectId,
      ...userData,
    };

    try {
      if (isEditMode) {
        setLoading("editEmployee", true);
        const response = await updateUserAssignedToProject(convertKeysToSnakeCase(payload));
        if (response.ok) {
          toast.success("Employee updated successfully");
          await refreshData();
          setIsUserPopupOpen(false);
          setIsEditMode(false);
          setEditEmployeeData(null);
        } else {
          const errorData = await response.json();
         setErrorMessages(prev => ({...prev, updateEmployee: errorData.detail}));

          console.error("API Error:", errorData);
        }
      } else {
        setLoading("assignEmployee", true);
        const response = await assignUserToProject(convertKeysToSnakeCase(payload));
        if (response.ok) {
          toast.success("Employee assigned successfully");
          await refreshData();
          setIsUserPopupOpen(false);
          setIsEditMode(false);
          setEditEmployeeData(null);
        } else {
          const errorData = await response.json();
          setErrorMessages(prev => ({...prev, addEmployee: errorData.detail}));
          console.error("API Error:", errorData);
        }
      }
    } catch (error) {
      toast.error(isEditMode ? "Failed to update employee" : "Failed to assign employee");
    } finally {
      setLoading(isEditMode ? "editEmployee" : "assignEmployee", false);
    }

    
  };

  const handleEditClick = (employee) => {
    setErrorMessages(prev => ({...prev, updateEmployee: ""}));
    setEditEmployeeData(employee);
    setIsEditMode(true);
    setIsUserPopupOpen(true);
  };

  const handleOffboard = (employee) => {
    if (getEmployeeStatus(employee) === "OFFBOARDED") return;
    setUserToOffboard(employee);
    setErrorMessages(prev => ({...prev, updateEmployeeStatus: ""}));
    setIsOffboardModalOpen(true);
  };

  const handleDelete = async (employee) => {
    if (getEmployeeStatus(employee) === "OFFBOARDED") return;
    setUserToDelete(employee);
    setIsDeleteModalOpen(true);
    setIsCheckingDelete(true);
    const payload = {
      projectId: projectId,
      id: employee?.id,
      force_delete: false
    };

    try {
      const response = await deleteUserAssignedToProject(convertKeysToSnakeCase(payload));
      const data = await response.json();
      
      if (data.count === true) {
        setDeleteWorklogCount(true);
      }
    } catch (error) {
      console.error("Error checking delete:", error);
    } finally {
      setIsCheckingDelete(false);
    }
  };

  const cancelDelete = () => {
    setUserToDelete(null);
    setIsDeleteModalOpen(false);
    setDeleteWorklogCount(null);
    setForceDelete(false);
    setIsCheckingDelete(false);
  };

  const confirmDelete = async () => {
    if (userToDelete) {
      const payload = {
        projectId: projectId,
        id: userToDelete?.id,
        forceDelete: deleteWorklogCount ? forceDelete : true
      };

      try {
        setLoading("deleteEmployee", true);
        const response = await deleteUserAssignedToProject(convertKeysToSnakeCase(payload));
        if (response.ok) {
          toast.success("Employee deleted successfully");
          await refreshData();
          setIsDeleteModalOpen(false);
          setUserToDelete(null);
          setDeleteWorklogCount(null);
          setForceDelete(false);
        } else {
          const errorData = await response.json();
          toast.error("Failed to delete employee");
          console.error("API Error:", errorData);
        }
      } catch (error) {
        toast.error("Failed to delete employee");
      } finally {
        setLoading("deleteEmployee", false);
      }
    }
  };

  const handleViewWorklogs = () => {
    navigate(`/worklogs_project/${id}`);
  };

  const handleAssignUser = () => {
    setIsEditMode(false);
    setEditEmployeeData(null);
    setErrorMessages(prev => ({...prev, addEmployee: ""}));
    setIsUserPopupOpen(true);
  };

  const getEmployeeStatus = (employee) => {
    return employee.employeeStatus || "ACTIVE";
  };

  const formatDate = (date) => {
    if (!date || date === null || date === undefined) {
      return "-";
    }
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) {
      return "-";
    }
    return parsedDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div>
      <div
        className="card shadow-sm p-3 m-4"
        style={{ width: "80vw", borderRadius: "12px" }}
      >
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-3">
          <div className="d-flex align-items-center gap-3">
            <div className="p-2 rounded border d-flex align-items-center justify-content-center bg-light">
              <Users size={20} />
            </div>
            <div>
              <h5 className="mb-1 fw-bold">Assigned Employees</h5>
              <small className="text-muted">
                {employees?.length} team member
                {employees?.length !== 1 ? "s" : ""} allocated
              </small>
            </div>
          </div>
          <div className="d-flex gap-2">
            {canAssignEmployee && (
              <button
                className={`btn btn-sm d-flex align-items-center gap-2 ${
                  status === "CLOSED" ? "btn-secondary" : "btn-primary"
                }`}
                onClick={handleAssignUser}
                disabled={status === "CLOSED"}
              >
                <User size={16} />
                Assign Employee
              </button>
            )}
            <button
              className={`btn btn-sm d-flex align-items-center gap-2 ${
                canViewAllWorklogs ? "btn-primary" : "btn-secondary"
              }`}
              onClick={handleViewWorklogs}
              disabled={!canViewAllWorklogs}
            >
              <ClipboardList size={16} />
              View Worklogs
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th className="info-item-label" style={{ color: "#6c757d" }}>
                  <Hash size={16} className="me-1 " />
                  Employee ID
                </th>
                <th className="info-item-label" style={{ color: "#6c757d" }}>
                  <Users size={16} className="me-1" />
                  Employee Name
                </th>
                <th className="info-item-label" style={{ color: "#6c757d" }}>
                  <Package size={16} className="me-1" />
                  Module Info
                </th>
                <th className="info-item-label" style={{ color: "#6c757d" }}>
                  <Calendar size={16} className="me-1" />
                  Status
                </th>
                <th className="info-item-label" style={{ color: "#6c757d" }}>
                  <Package size={16} className="me-1" />
                  Billing Type
                </th>
                <th className="info-item-label" style={{ color: "#6c757d" }}>
                  <Package size={16} className="me-1" />
                  Billing Amount
                </th>

                <th className="info-item-label" style={{ color: "#6c757d" }}>
                  <Calendar size={16} className="me-1" />
                  Onboarding Date
                </th>
                <th className="info-item-label" style={{ color: "#6c757d" }}>
                  <Calendar size={16} className="me-1" />
                  Offboarding Date
                </th>

                <th
                  className=" info-item-label"
                  style={{ color: "#6c757d" }}
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {employees && employees.length > 0 ? (
                employees.map((employee, index) => (
                  <tr key={`${employee.employeeId}-${index}`}>
                    <td className="info-item-value" style={{ textAlign: 'center' }}>{employee.employeeId}</td>
                    <td className="info-item-value" style={{ textAlign: 'center'}}>
                      {employee.employeeName}
                    </td>
                    <td className="info-item-value" style={{  textAlign: 'center' }}>
                      <span>
                        {employee.modules && employee.modules.length > 0
                          ? employee.modules
                              .map((module) => module.name)
                              .join(", ")
                          : "No Modules Assigned"}
                      </span>
                    </td>
                    <td className="info-item-value" style={{  textAlign: 'center' }}>
                      <span
                        className={`badge ${
                          STATUS_BADGE_CONFIG[getEmployeeStatus(employee)]
                            ?.className || STATUS_BADGE_CONFIG.DEFAULT.className
                        }`}
                      >
                        {STATUS_BADGE_CONFIG[getEmployeeStatus(employee)]
                          ?.label || STATUS_BADGE_CONFIG.DEFAULT.label}
                      </span>
                    </td>

                    <td className="info-item-value" style={{ textAlign: 'center' }}>{employee.billingType}</td>
                    <td className="info-item-value" style={{ textAlign: 'center' }}>
                      {employee.billingAmount}
                    </td>
                    <td className="info-item-value" style={{ textAlign: 'center' }}>
                      {formatDate(employee.assignedAt)}
                    </td>
                    <td className="info-item-value" style={{ textAlign: 'center' }}>
                      {formatDate(employee.releasedAt)}
                    </td>

                    <td className=" info-item-value">
                      <div className="d-flex justify-content-start gap-1 info-item-value">
                        {canEditEmployee && (
                          <button
                            className={`btn btn-sm icon-wrapper ${
                              getEmployeeStatus(employee) === "OFFBOARDED"
                                ? "border-0"
                                : ""
                            }`}
                            title="Edit"
                            onClick={() => handleEditClick(employee)}
                            disabled={
                              getEmployeeStatus(employee) === "OFFBOARDED"
                            }
                          >
                            <Pencil size={16} style={{ color: getEmployeeStatus(employee) === "OFFBOARDED" ? "#6c757d" : "#28a745" }} />
                          </button>
                        )}
                        {canReleaseEmployee && (
                          <button
                            className={`btn btn-sm icon-wrapper ${
                              getEmployeeStatus(employee) === "OFFBOARDED"
                                ? "border-0"
                                : ""
                            }`}
                            onClick={() => handleOffboard(employee)}
                            disabled={
                              getEmployeeStatus(employee) === "OFFBOARDED"
                            }
                            title="OffBoard Employee"
                          >
                            <Ban
                              size={16}
                              style={{
                                color:
                                  getEmployeeStatus(employee) === "OFFBOARDED"
                                    ? "#6c757d"
                                    : "#dc3545",
                              }}
                            />
                          </button>
                        )}
                        {canRemoveEmployee && (
                        <button
                          className={`btn btn-sm icon-wrapper ${
                            getEmployeeStatus(employee) === "OFFBOARDED"
                              ? "border-0"
                              : ""
                          }`}
                          onClick={() => handleDelete(employee)}
                          disabled={
                            getEmployeeStatus(employee) === "OFFBOARDED"
                          }
                          title="Delete"
                        >
                          <Trash
                            size={16}
                            style={{
                              color:
                                getEmployeeStatus(employee) === "OFFBOARDED"
                                  ? "#6c757d"
                                  : "#dc3545",
                            }}
                          />
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-muted">
                    <div className="d-flex flex-column align-items-center gap-2">
                      <span>No employees associated with this project</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit User Modal */}
      <PopupModal
        showModal={isUserPopupOpen}
        modalName={
          isEditMode
            ? "Update Employee Assignment"
            : "Assign Employee to Project"
        }
        closeBtn={handleUserCancel}
      >
        <AddProjectAssignmentForm
        isLoading={loadingStates?.editEmployee || loadingStates?.assignEmployee}
          onSubmit={handleUserSubmit}
          onCancel={handleUserCancel}
          selectedProjectId={projectId}
          editData={isEditMode ? editEmployeeData : null}
          projectStartDate={startDate}
          errorMsg={ errorMessages.updateEmployee || errorMessages.addEmployee}
          onClearError={() => setErrorMessages(prev => ({...prev, updateEmployee: "", addEmployee: ""}))}

        />
      </PopupModal>

      {/* Delete Confirmation Modal */}
      <div className="no-modal-mb">
        <PopupModal
          showModal={isDeleteModalOpen}
          modalName="Confirm Delete"
          closeBtn={cancelDelete}
          marginBottom="0px"
        >
          <div>
            {isCheckingDelete ? (
              <p>Loading.....</p>
            ) : deleteWorklogCount ? (
              <>
                <p style={{ maxWidth: "550px" }}>
                  The "{userToDelete?.employeeName}" employee has existing worklogs. Deleting the employee will remove all of those worklogs as well.
                </p>
                <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center" , justifyContent:"end" }}>
                  <input
                    type="checkbox"
                    id="forceDeleteCheck"
                    checked={forceDelete}
                    onChange={(e) => setForceDelete(e.target.checked)}
                    style={{ marginRight: "8px", cursor: "pointer" }}
                  />
                  <span style={{ cursor: "pointer" }} onClick={() => setForceDelete(!forceDelete)}>
                    Force delete?
                  </span>
                </div>
                {forceDelete && (
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "end",
                      marginTop: "15px",
                    }}
                  >
                    <SecondaryButton onClick={cancelDelete}>Cancel</SecondaryButton>
                    <PrimaryButton 
                      onClick={confirmDelete} 
                      disabled={loadingStates?.deleteEmployee}
                    >
                      {loadingStates?.deleteEmployee ? 'Deleting...' : 'Delete'}
                    </PrimaryButton>
                  </div>
                )}
              </>
            ) : (
              <>
                <p>Are you sure you want to delete this employee?</p>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "end",
                    marginTop: "15px",
                  }}
                >
                  <SecondaryButton onClick={cancelDelete}>Cancel</SecondaryButton>
                  <PrimaryButton 
                    onClick={confirmDelete} 
                    disabled={isCheckingDelete || loadingStates?.deleteEmployee}
                  >
                    {loadingStates?.deleteEmployee ? 'Deleting...' : 'Delete'}
                  </PrimaryButton>
                </div>
              </>
            )}
          </div>
        </PopupModal>
      </div>

      {/* Offboard Confirmation Modal */}
      <div className="no-modal-mb">
        <PopupModal
          showModal={isOffboardModalOpen}
          modalName="Confirm Offboarding"
          closeBtn={cancelOffboard}
          marginBottom="0px"
        >
          <div>
            <p>Select offboarding date:</p>
            <input
              type="date"
              value={offboardingDate}
              min={userToOffboard?.assignedAt?.split(" ")[0]}
              onChange={(e) => {
                setOffboardingDate(e.target.value);
                setErrorMessages(prev => ({...prev, updateEmployeeStatus: ""}));
              }}
              className="form-control mb-3"
               max="9999-12-31"

            />
            {errorMessages.updateEmployeeStatus && (
              <ErrorText className="mt-3">*{errorMessages.updateEmployeeStatus}</ErrorText>
            )}
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "end",
                marginTop: "15px",
              }}
            >
              <SecondaryButton onClick={cancelOffboard}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                onClick={handleDateSubmit}
                disabled={!offboardingDate || loadingStates?.updateEmployeeStatus || errorMessages.updateEmployeeStatus}
              >
                {loadingStates?.updateEmployeeStatus ? 'Confirming...' : 'Confirm'}
              </PrimaryButton>
            </div>
          </div>
        </PopupModal>
      </div>
    </div>
  );
};
